/**
 * My Samadhan Journey — User Dashboard widget
 * Shows user's donation summary, earned tier, certificate list.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Award, ArrowRight, Share2 } from 'lucide-react';
import api from '../utils/api';

const fmtAT = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const TIER_META = {
  'DIVINE BASE BUILDER': { icon: '🕉️', color: 'from-violet-500 via-fuchsia-400 to-amber-300', rank: 5, threshold: 50000000 },
  'SUPER BASE BUILDER':  { icon: '👑', color: 'from-indigo-500 to-violet-600',                rank: 4, threshold: 10000000 },
  'VVIP BASE BUILDER':   { icon: '💎', color: 'from-purple-500 to-fuchsia-600',               rank: 3, threshold: 5000000 },
  'VIP BASE BUILDER':    { icon: '⭐', color: 'from-fuchsia-500 to-pink-500',                 rank: 2, threshold: 2500000 },
  'BASE BUILDER':        { icon: '🏛️', color: 'from-amber-500 to-orange-500',                 rank: 1, threshold: 1000000 },
};
const TIER_LADDER = [
  { key: 'BASE BUILDER',        threshold: 1000000 },
  { key: 'VIP BASE BUILDER',    threshold: 2500000 },
  { key: 'VVIP BASE BUILDER',   threshold: 5000000 },
  { key: 'SUPER BASE BUILDER',  threshold: 10000000 },
  { key: 'DIVINE BASE BUILDER', threshold: 50000000 },
];

const SamadhanJourneyWidget = () => {
  const [data, setData] = useState(null);
  const [team, setTeam] = useState(null);
  useEffect(() => {
    api.get('/samadhan/my-purchases')
      .then((r) => setData(r.data))
      .catch(() => setData({ purchases: [], total_donated: 0, count: 0 }));
    api.get('/samadhan/my-team')
      .then((r) => setTeam(r.data))
      .catch(() => setTeam({ members: [], top5: [], summary: { total_members: 0, total_downline_donated: 0, total_earned: 0 } }));
  }, []);

  if (!data) return null;
  const { purchases = [], total_donated = 0, count = 0 } = data;

  // Compute highest tier and total visits
  const withNameplate = purchases.filter((p) => p.nameplate);
  const topTier = withNameplate.reduce((best, p) => {
    const r = TIER_META[p.nameplate]?.rank || 0;
    return r > (best.rank || 0) ? { ...TIER_META[p.nameplate], label: p.nameplate, rank: r } : best;
  }, { rank: 0 });
  const totalVisits = purchases.reduce((s, p) => s + Number(p.visits || 0), 0);

  const share = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const uname = purchases[0]?.username || '';
    const url = `${origin}/samadhan${uname ? `?ref=${uname}` : ''}`;
    const msg = `🙏 Meri Samadhan Center Journey:\n\n` +
      `💎 Total Donated: ${fmtAT(total_donated)} AT\n` +
      `📜 Certificates: ${count}\n` +
      (topTier.label ? `🏆 Earned Tier: ${topTier.icon} ${topTier.label}\n` : '') +
      (totalVisits ? `🌟 Visit Passes: ${totalVisits}\n` : '') +
      `\n🚨 The Dream Project · Live 01/01/2031 · Paratwada\n` +
      `Aap bhi is divine mission mein judiye:\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div
      className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/60 via-slate-950 to-fuchsia-950/40 p-5 relative overflow-hidden mb-6"
      data-testid="samadhan-journey-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-amber-300" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">॥ My Samadhan Journey ॥</span>
      </div>

      {count === 0 ? (
        <div className="text-center py-4">
          <div className="text-5xl mb-2">🏛️</div>
          <h3 className="text-lg font-black bg-gradient-to-r from-amber-300 to-fuchsia-300 bg-clip-text text-transparent">Aapki journey abhi start honi hai</h3>
          <p className="text-xs text-slate-300 mt-1">Samadhan Center — The Dream Project (Live 01/01/2031). Contribute karke apna certificate + lifetime tier badge kamayein.</p>
          <Link to="/samadhan" data-testid="journey-start-cta" className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-fuchsia-500 text-white text-sm font-bold hover:scale-105 transition">
            🙏 Start Your Journey <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">Total Donated</div>
              <div className="text-lg md:text-xl font-black text-emerald-300 mt-0.5">{fmtAT(total_donated)}<span className="text-xs opacity-70 ml-1">AT</span></div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">Certificates</div>
              <div className="text-lg md:text-xl font-black text-fuchsia-300 mt-0.5">{count}</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">Visit Passes</div>
              <div className="text-lg md:text-xl font-black text-sky-300 mt-0.5">{totalVisits || '—'}</div>
            </div>
            <div className={`rounded-xl border p-3 ${topTier.label ? 'border-amber-400/60' : 'border-white/10 bg-white/5'}`} style={topTier.label ? { background: 'linear-gradient(135deg, rgba(180,83,9,0.25), rgba(251,191,36,0.15))' } : {}}>
              <div className="text-[10px] text-slate-300 uppercase tracking-wider">Earned Tier</div>
              {topTier.label ? (
                <div className="mt-0.5">
                  <div className="text-2xl">{topTier.icon}</div>
                  <div className="text-[10px] font-black text-amber-200 leading-tight">{topTier.label}</div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 mt-1 italic">Aim for BASE BUILDER (10L AT+)</div>
              )}
            </div>
          </div>

          {/* Progress to Next Tier */}
          {(() => {
            const nextTier = TIER_LADDER.find((t) => total_donated < t.threshold);
            if (!nextTier) {
              // User is already at DIVINE — highest tier
              return (
                <div className="rounded-xl border-2 border-amber-400/60 bg-gradient-to-r from-amber-950 via-fuchsia-900 to-violet-950 p-3 mb-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🕉️ MAXIMUM DIVINE STATUS ACHIEVED</div>
                  <div className="text-sm text-amber-100 mt-1">Aap SAMADHAN CENTER ke sabse ooncha tier par pahunch chuke hain. Dhanyawad 🙏</div>
                </div>
              );
            }
            const meta = TIER_META[nextTier.key] || {};
            const prevThreshold = TIER_LADDER[TIER_LADDER.indexOf(nextTier) - 1]?.threshold || 0;
            const rangeSpan = nextTier.threshold - prevThreshold;
            const donatedInRange = Math.max(0, total_donated - prevThreshold);
            const pct = rangeSpan > 0 ? Math.min(100, (donatedInRange / rangeSpan) * 100) : 0;
            const remaining = Math.max(0, nextTier.threshold - total_donated);
            return (
              <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3 mb-3" data-testid="journey-next-tier">
                <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                  <div className="text-[11px] font-bold text-white">
                    Next Tier: <span className="text-amber-300">{meta.icon} {nextTier.key}</span>
                  </div>
                  <div className="text-[10px] text-red-300 font-bold">
                    ₹{remaining.toLocaleString('en-IN')} AT away
                  </div>
                </div>
                <div className="relative w-full h-3 rounded-full bg-white/5 overflow-hidden border border-white/10">
                  <div className={`h-full bg-gradient-to-r ${meta.color || 'from-amber-500 to-fuchsia-500'} transition-all`} style={{ width: `${pct}%` }} />
                  <div className="absolute inset-0 flex items-center justify-center text-[9px] font-black text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>
                    {pct.toFixed(1)}% to {meta.icon} {nextTier.key}
                  </div>
                </div>
                <div className="mt-1.5 text-[10px] text-slate-400 flex items-center justify-between">
                  <span>Current: <b className="text-emerald-300">{fmtAT(total_donated)} AT</b></span>
                  <span>Goal: <b className="text-fuchsia-300">{fmtAT(nextTier.threshold)} AT</b></span>
                </div>
                <div className="mt-1 text-[10px] text-amber-200/80 italic text-center">
                  💡 Just {fmtAT(remaining)} AT more to unlock a lifetime {nextTier.key} nameplate!
                </div>
              </div>
            );
          })()}

          {/* Recent certificates preview */}
          <div className="rounded-xl border border-white/10 bg-black/30 p-3 mb-3">
            <div className="text-[10px] font-bold text-amber-200 uppercase tracking-wider mb-1.5">📜 Recent Certificates ({Math.min(3, purchases.length)} of {count})</div>
            <div className="space-y-1.5">
              {purchases.slice(0, 3).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs" data-testid={`journey-cert-${p.cert_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-bold truncate">{p.pack_label}</div>
                    <div className="text-[10px] text-slate-400 truncate">
                      <span className="font-mono">{p.cert_id}</span> · {new Date(p.created_at).toLocaleDateString('en-IN')}
                      {p.nameplate && <span className="ml-1 text-amber-300"> · {TIER_META[p.nameplate]?.icon} {p.nameplate}</span>}
                    </div>
                  </div>
                  <div className="text-[11px] font-black text-emerald-300 font-mono ml-2">{fmtAT(p.amount)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Link to="/samadhan" data-testid="journey-donate-more" className="flex-1 min-w-[140px] px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-fuchsia-500 text-white text-sm font-bold flex items-center justify-center gap-1 hover:scale-[1.02] transition">
              <Award className="w-4 h-4" /> Donate More · Climb Tier
            </Link>
            <button onClick={share} data-testid="journey-share" className="flex-1 min-w-[140px] px-4 py-2 rounded-xl bg-green-500 hover:bg-green-400 text-white text-sm font-bold flex items-center justify-center gap-1">
              <Share2 className="w-4 h-4" /> Share My Journey
            </button>
            <Link to="/samadhan/gallery" data-testid="journey-gallery" className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-slate-200 text-xs flex items-center gap-1">
              🖼️ Gallery
            </Link>
          </div>

          {/* Team Contribution Board */}
          {team && team.summary && team.summary.total_members > 0 && (
            <div className="mt-3 rounded-xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950/50 via-slate-900/80 to-indigo-950/40 p-3" data-testid="journey-team-board">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">🏆 Team Contribution Board</span>
                </div>
                <div className="text-[10px] text-slate-400">
                  {team.summary.total_members} downline · earned <b className="text-emerald-300 font-mono">₹{fmtAT(team.summary.total_earned)} AT</b>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
                  <div className="text-[9px] text-slate-400 uppercase">Team Members</div>
                  <div className="text-base font-black text-sky-300">{team.summary.total_members}</div>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
                  <div className="text-[9px] text-slate-400 uppercase">Downline Donated</div>
                  <div className="text-base font-black text-fuchsia-300">{fmtAT(team.summary.total_downline_donated)}</div>
                </div>
                <div className="rounded-lg border-2 border-emerald-400/50 p-2 text-center" style={{ background: 'linear-gradient(135deg, rgba(6,95,70,0.4), rgba(16,185,129,0.15))' }}>
                  <div className="text-[9px] text-emerald-200 uppercase font-bold">Your Earnings</div>
                  <div className="text-base font-black text-emerald-300">{fmtAT(team.summary.total_earned)} AT</div>
                </div>
              </div>
              {team.top5 && team.top5.length > 0 && (
                <div className="rounded-lg bg-black/40 border border-white/10 p-2">
                  <div className="text-[9px] font-bold text-amber-200 uppercase tracking-wider mb-1">🥇 Top 5 Contributors in Your Team</div>
                  {team.top5.map((m, i) => (
                    <div key={`${m.user_id}-${m.level}`} className="flex items-center justify-between text-[11px] py-0.5" data-testid={`team-member-${i}`}>
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <span className="text-amber-300 font-black">#{i + 1}</span>
                        <span className="text-white font-bold truncate">{m.full_name}</span>
                        <span className="text-[9px] text-slate-500">L{m.level}</span>
                        {m.highest_nameplate && TIER_META[m.highest_nameplate] && <span className="text-xs">{TIER_META[m.highest_nameplate].icon}</span>}
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="text-fuchsia-300 font-mono text-[10px]">{fmtAT(m.total_donated)}</div>
                        <div className="text-emerald-300 font-mono text-[9px]">+{fmtAT(m.total_earned)} earned</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-1.5 text-[9px] text-emerald-200/80 italic text-center">
                💡 Har team member ke Samadhan donation par aap 7-tier Affiliate Network commission earn karte hain. Team ko motivate karo!
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SamadhanJourneyWidget;
